import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import type { User, Venue, VenueGroup } from '@/types';

const userSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional().or(z.literal('')),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.enum(['system_admin', 'group_admin', 'group_operator', 'venue_admin', 'venue_operator', 'guest']),
  venueId: z.string().optional().or(z.literal('')),
  groupId: z.string().optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});

type UserFormData = z.infer<typeof userSchema>;

export default function UserForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupError, setNewGroupError] = useState('');

  const { data: user, isLoading: userLoading } = useQuery<User>({
    queryKey: ['user', id],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${id}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const { data: venues } = useQuery<{ data: Venue[] }>({
    queryKey: ['venues-list'],
    queryFn: async () => {
      const response = await api.get('/admin/venues?limit=100');
      return response.data;
    },
  });

  const { data: groups, refetch: refetchGroups } = useQuery<VenueGroup[]>({
    queryKey: ['groups-all'],
    queryFn: async () => {
      const response = await api.get('/admin/groups/all');
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'venue_operator',
      venueId: '',
      groupId: '',
      isActive: true,
    },
  });

  const role = watch('role');

  useEffect(() => {
    if (user) {
      const u = user as unknown as Record<string, unknown>;
      reset({
        email: u.email as string,
        password: '',
        firstName: u.first_name as string,
        lastName: u.last_name as string,
        role: u.role as 'system_admin' | 'group_admin' | 'group_operator' | 'venue_admin' | 'venue_operator' | 'guest',
        venueId: (u.venue_id as string) || '',
        groupId: (u.group_id as string) || '',
        isActive: (u.is_active as boolean) ?? true,
      });
    }
  }, [user, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const payload = {
        ...data,
        venueId: data.venueId || undefined,
        groupId: data.groupId || undefined,
      };
      if (!payload.password) {
        delete (payload as Record<string, unknown>).password;
      }
      const response = await api.post('/admin/users', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const payload: Record<string, unknown> = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        isActive: data.isActive,
      };
      if (data.venueId) payload.venueId = data.venueId;
      if (data.groupId) payload.groupId = data.groupId;

      await api.put(`/admin/users/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      navigate('/users');
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: async (name: string) => {
      const slug = name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
      const response = await api.post('/admin/groups', { name, slug });
      return response.data;
    },
    onSuccess: (data) => {
      refetchGroups();
      setValue('groupId', data.id);
      setShowGroupModal(false);
      setNewGroupName('');
      setNewGroupError('');
    },
    onError: (error: Error) => {
      setNewGroupError(error.message || 'Failed to create group');
    },
  });

  const onSubmit = async (data: UserFormData) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) {
      setNewGroupError('Group name is required');
      return;
    }
    createGroupMutation.mutate(newGroupName.trim());
  };

  const showVenueField = ['venue_admin', 'venue_operator'].includes(role);
  const showGroupField = ['group_admin', 'group_operator'].includes(role);

  if (isEditing && userLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit User' : 'Create User'}
        </h1>
      </div>

      <div className="card max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {(createMutation.error as Error)?.message || (updateMutation.error as Error)?.message || 'An error occurred'}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="label">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                className="input"
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="label">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                className="input"
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              className="input"
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {!isEditing && (
            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                {...register('password')}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Minimum 8 characters
              </p>
            </div>
          )}

          <div>
            <label htmlFor="role" className="label">
              Role
            </label>
            <select id="role" className="input" {...register('role')}>
              <option value="system_admin">System Admin</option>
              <option value="group_admin">Group Admin</option>
              <option value="group_operator">Group Operator</option>
              <option value="venue_admin">Venue Admin</option>
              <option value="venue_operator">Venue Operator</option>
              <option value="guest">Guest</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          {showVenueField && (
            <div>
              <label htmlFor="venueId" className="label">
                Assigned Venue
              </label>
              <select id="venueId" className="input" {...register('venueId')}>
                <option value="">Select a venue...</option>
                {venues?.data.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                The venue this user will manage
              </p>
            </div>
          )}

          {showGroupField && (
            <div>
              <label htmlFor="groupId" className="label">
                Assigned Group
              </label>
              <div className="flex gap-2">
                <select id="groupId" className="input flex-1" {...register('groupId')}>
                  <option value="">Select a group...</option>
                  {groups?.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowGroupModal(true)}
                  className="btn btn-secondary"
                  title="Create new group"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                The venue group this user will manage
              </p>
            </div>
          )}

          {isEditing && (
            <div className="flex items-center gap-3">
              <input
                id="isActive"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('isActive')}
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                User is active and can log in
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update User' : 'Create User'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Create Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Create New Group</h2>
              <button
                onClick={() => {
                  setShowGroupModal(false);
                  setNewGroupName('');
                  setNewGroupError('');
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {newGroupError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {newGroupError}
              </div>
            )}

            <div className="mb-4">
              <label className="label">Group Name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g. City Centre Restaurants"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateGroup();
                  }
                }}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowGroupModal(false);
                  setNewGroupName('');
                  setNewGroupError('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateGroup}
                disabled={createGroupMutation.isPending}
                className="btn btn-primary"
              >
                {createGroupMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Group'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
